import { create } from 'zustand'
import type { CryptoAmount, FiatAmount } from '@/types'
import type { WalletCurrency } from '@/domain'
import type { BlockchainCatalogsDTO } from '@/domain/blockchains/blockchainsApi'
import type { CreateCryptoAddressWithdrawPayload } from '@/domain/wallet/walletTypes'

// Wallet address types (same as RN)
export interface CryptoAddressWithdraw {
  id?: string
  name: string
  address: string
}

// Use type from API instead of a custom one
export type BlockchainCatalog = BlockchainCatalogsDTO

interface WithdrawState {
  // Input mode (fiat or crypto)
  isFiatMode: boolean

  // Shake animation for validation
  triggerShake: boolean

  // Amounts
  cryptoAmount: CryptoAmount
  fiatAmount: FiatAmount

  // Selected currency from wallet
  selectedCryptoWalletData?: WalletCurrency

  // External wallet address for withdrawal
  externalWalletAddress: CryptoAddressWithdraw

  // Wallet address registration form
  walletAddressRegisterForm: CryptoAddressWithdraw

  // Selected blockchain
  blockchain?: BlockchainCatalog

  // UI state for currency modal
  isCurrencyModalOpen: boolean

  // UI state for blockchain modal
  isBlockchainModalOpen: boolean

  // MFA payload for persisting data between screens
  mfaPayload?: Partial<CreateCryptoAddressWithdrawPayload>
}

interface WithdrawActions {
  // Toggle input mode
  setIsFiatMode: (isFiatMode: boolean) => void

  // Amount management
  setCryptoAmount: (cryptoAmount: CryptoAmount) => void
  setFiatAmount: (fiatAmount: FiatAmount) => void

  // Selected currency management
  setSelectedCryptoWalletData: (
    selectedCryptoWalletData: WalletCurrency,
  ) => void

  // Shake animation
  shakeComponent: () => void

  // Blockchain management
  setBlockchain: (blockchain: BlockchainCatalog) => void

  // External wallet address management
  setExternalWalletAddress: (
    externalWalletAddress: Partial<CryptoAddressWithdraw>,
  ) => void
  resetExternalWalletAddress: () => void

  // Address registration form management
  setWalletAddressRegisterForm: (
    walletAddressRegisterForm: Partial<CryptoAddressWithdraw>,
  ) => void
  resetWalletAddressForm: () => void

  // UI actions for currency modal
  setIsCurrencyModalOpen: (open: boolean) => void

  // UI actions for blockchain modal
  setIsBlockchainModalOpen: (open: boolean) => void

  // MFA payload management
  setMfaPayload: (
    mfaPayload: Partial<CreateCryptoAddressWithdrawPayload>,
  ) => void
  resetMfaPayload: () => void

  // Full store reset
  resetWithdrawStore: () => void
}

type WithdrawStore = WithdrawState & WithdrawActions

// Initial state
const initialState: WithdrawState = {
  isFiatMode: false,
  triggerShake: false,
  selectedCryptoWalletData: undefined,
  fiatAmount: {
    amount: '0',
    code: 'USD',
  },
  cryptoAmount: {
    amount: '0',
    code: 'USDT',
  },
  externalWalletAddress: {
    id: '',
    name: '',
    address: '',
  },
  walletAddressRegisterForm: {
    id: '',
    name: '',
    address: '',
  },
  blockchain: undefined,
  isCurrencyModalOpen: false,
  isBlockchainModalOpen: false,
  mfaPayload: {
    address: '',
    name: '',
    network_code: '',
  },
}

export const useWithdrawStore = create<WithdrawStore>()((set) => ({
  ...initialState,

  // Toggle input mode
  setIsFiatMode: (isFiatMode) => set({ isFiatMode }),

  // Amount management
  setCryptoAmount: (cryptoAmount) => set({ cryptoAmount }),
  setFiatAmount: (fiatAmount) => set({ fiatAmount }),

  // Selected currency management
  setSelectedCryptoWalletData: (selectedCryptoWalletData) =>
    set((state) => {
      // If currency changed, clear amounts
      const currencyChanged =
        state.selectedCryptoWalletData?.code !== selectedCryptoWalletData.code

      if (currencyChanged) {
        // Preserve current fiat currency code when resetting amount
        const resetFiatAmount = { amount: '0', code: state.fiatAmount.code }
        const resetCryptoAmount = {
          amount: '0',
          code: selectedCryptoWalletData.code,
        }

        return {
          selectedCryptoWalletData,
          fiatAmount: resetFiatAmount,
          cryptoAmount: resetCryptoAmount,
        }
      }

      // If currency didn't change, just update selectedCryptoWalletData
      return { selectedCryptoWalletData }
    }),

  // Shake animation
  shakeComponent: () => {
    set({ triggerShake: true })
    setTimeout(() => set({ triggerShake: false }), 600)
  },

  // Blockchain management
  setBlockchain: (blockchain) => set({ blockchain }),

  // External wallet address management
  setExternalWalletAddress: (externalWalletAddress) =>
    set((state) => ({
      externalWalletAddress: {
        ...state.externalWalletAddress,
        ...externalWalletAddress,
      },
    })),

  resetExternalWalletAddress: () =>
    set({
      externalWalletAddress: initialState.externalWalletAddress,
    }),

  // Address registration form management
  setWalletAddressRegisterForm: (walletAddressRegisterForm) =>
    set((state) => ({
      walletAddressRegisterForm: {
        ...state.walletAddressRegisterForm,
        ...walletAddressRegisterForm,
      },
    })),

  resetWalletAddressForm: () =>
    set({ walletAddressRegisterForm: initialState.walletAddressRegisterForm }),

  // UI actions for currency modal
  setIsCurrencyModalOpen: (isCurrencyModalOpen) => set({ isCurrencyModalOpen }),

  // UI actions for blockchain modal
  setIsBlockchainModalOpen: (isBlockchainModalOpen) =>
    set({ isBlockchainModalOpen }),

  // MFA payload management
  setMfaPayload: (mfaPayload) =>
    set((state) => ({ mfaPayload: { ...state.mfaPayload, ...mfaPayload } })),

  resetMfaPayload: () => set({ mfaPayload: initialState.mfaPayload }),

  // Full store reset
  resetWithdrawStore: () =>
    set(({ selectedCryptoWalletData }) => ({
      ...initialState,
      selectedCryptoWalletData,
    })),
}))
